import type { ReploProject } from "schemas/generated/project";

import * as React from "react";

import {
  v2CustomDomainRouter,
  v2CustomDomainUtils,
} from "@editor/trpc/v2CustomDomain";

import { infoToast } from "@replo/design-system/components/alert/Toast";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { skipToken } from "@tanstack/react-query";
import { RefreshCw, Trash2 } from "lucide-react";
import { truncateDomain } from "replo-utils/lib/url";

interface DomainData {
  domain: string;
  id?: string;
  canBeDeleted: boolean;
  status: "verified" | "pending";
}

const DomainEntry = ({
  index,
  customDomain,
  customDomainId,
  canBeDeleted,
  status = "verified",
  onDeleteDomain,
  onRefreshDomain,
  isRefreshing,
}: {
  index: number;
  customDomain: string;
  customDomainId?: string;
  canBeDeleted: boolean;
  status?: "verified" | "pending";
  onDeleteDomain: (customDomainId: string) => void;
  onRefreshDomain?: (domainValue: string) => void;
  isRefreshing?: boolean;
}) => {
  return (
    <div className="flex items-center">
      {/* NOTE (Cole, 2025-07-03): I set a static width here so numbers with multiple digits don't shift the layout */}
      <span className="text-base font-medium text-muted w-6">{index}</span>
      <div className="flex items-center justify-between w-full bg-sheet border-0.5 border-border gap-3 p-3 rounded-lg">
        <span className="text-base font-medium flex-1" title={customDomain}>
          {truncateDomain(customDomain)}
        </span>
        <div className="flex items-center gap-2">
          {status === "pending" && onRefreshDomain && (
            <IconButton
              variant="secondary"
              icon={<RefreshCw size={12} />}
              size="sm"
              tooltipText="Check if domain is ready"
              onClick={() => onRefreshDomain(customDomain)}
              isLoading={isRefreshing}
            />
          )}
          {canBeDeleted && customDomainId && (
            <IconButton
              variant="secondary"
              icon={<Trash2 size={12} />}
              size="sm"
              tooltipText="Remove domain"
              onClick={() => onDeleteDomain(customDomainId)}
            />
          )}
          {status === "pending" ? (
            <Tag variant="info" size="base">
              Pending
            </Tag>
          ) : (
            <Tag variant="success" size="base">
              Verified
            </Tag>
          )}
        </div>
      </div>
    </div>
  );
};

export const DomainsDisplay: React.FC<{
  project: ReploProject;
}> = ({ project }) => {
  const { data: customDomainsData } =
    v2CustomDomainRouter.getByProjectId.useQuery(
      project?.id ? { projectId: project.id } : skipToken,
    );

  const customDomains = customDomainsData?.customDomains ?? [];

  const { mutate: deleteCustomDomain } =
    v2CustomDomainRouter.deleteCustomDomainInDatabase.useMutation({
      onSuccess: () => {
        // Invalidate and refetch
        void v2CustomDomainUtils.getByProjectId.invalidate({
          projectId: project.id,
        });
      },
    });

  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const { mutateAsync: createCustomDomainInCloudflare } =
    v2CustomDomainRouter.createCustomDomainInCloudflare.useMutation();

  const { mutateAsync: markDomainAsVerified } =
    v2CustomDomainRouter.markDomainAsVerified.useMutation();

  const handleRefreshDomain = async (domainValue: string) => {
    setIsRefreshing(true);
    try {
      const { ok: cnameRecordIsAlreadySet } =
        await v2CustomDomainUtils.checkCustomDomainForReploshopsCname.fetch(
          {
            projectId: project.id,
            customDomain: domainValue,
          },
          {
            staleTime: 0, // Force fresh fetch every time
          },
        );

      if (cnameRecordIsAlreadySet) {
        // Find the pending domain
        const { customDomainId: pendingDomainId } =
          await v2CustomDomainUtils.findDomainByValue.fetch({
            projectId: project.id,
            customDomain: domainValue,
          });

        if (pendingDomainId) {
          // Create in Cloudflare and mark as verified
          const { cloudflareCustomHostnameId } =
            await createCustomDomainInCloudflare({
              projectId: project.id,
              customDomain: domainValue,
              workspaceId: project.ownerWorkspaceId,
            });

          await markDomainAsVerified({
            projectId: project.id,
            customDomainId: pendingDomainId,
            cloudflareCustomHostnameId,
          });

          // Invalidate to refresh the list
          void v2CustomDomainUtils.getByProjectId.invalidate({
            projectId: project.id,
          });
        }
      } else {
        infoToast(
          "Domain Not Ready",
          "CNAME record not yet propagated. Please wait a few minutes and try again.",
        );
      }
    } catch (error) {
      console.error("Error refreshing domain:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const domains: DomainData[] = [
    ...(project?.shopDomainPrefix
      ? [
          {
            domain: `${project.shopDomainPrefix}.reploshops.com`,
            canBeDeleted: false,
            status: "verified" as const,
          },
        ]
      : []),
    ...customDomains.map((domain) => ({
      domain: domain.value,
      id: domain.id,
      canBeDeleted: true,
      status: domain.isPending ? ("pending" as const) : ("verified" as const),
    })),
  ];

  const handleDeleteDomain = (customDomainId: string) => {
    deleteCustomDomain({
      projectId: project.id,
      customDomainId,
    });
  };

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex flex-col gap-2">
        {domains.map((domainData, index) => (
          <React.Fragment key={domainData.domain}>
            <DomainEntry
              index={index + 1}
              customDomain={domainData.domain}
              customDomainId={domainData.id}
              canBeDeleted={domainData.canBeDeleted}
              status={domainData.status}
              onDeleteDomain={handleDeleteDomain}
              onRefreshDomain={handleRefreshDomain}
              isRefreshing={isRefreshing}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
